import { useCallback, useState } from 'react';
import Notification, {
  NotificationContainer,
} from '../Notification';


export type NotifyLevel = 'error' | 'success';


interface NotifyPayload {
  title: string;
  message: string;
}


interface NotificationsState {
  error: NotifyPayload | null;
  success: NotifyPayload | null;
  /** Surface an error toast. */
  notifyError: (payload: NotifyPayload) => void;
  /** Surface a success toast. */
  notifySuccess: (payload: NotifyPayload) => void;
  /** Normalise an axios/fetch failure into an error toast. */
  notifyFailure: (title: string, error: unknown) => void;
  clear: () => void;
}


/** Hook that encapsulates the "show an error or success toast" pattern
 *  duplicated across RemediationsPanel, RiskAcceptancesPanel,
 *  EvidencePanel (and similar). Render with the `PanelNotifications`
 *  component (or your own positioning wrapper) to display the toasts. */
export function usePanelNotifications(): NotificationsState {
  const [error, setError] = useState<NotifyPayload | null>(null);
  const [success, setSuccess] = useState<NotifyPayload | null>(null);

  const notifyError = useCallback((payload: NotifyPayload) => {
    setError(payload);
  }, []);

  const notifySuccess = useCallback((payload: NotifyPayload) => {
    setSuccess(payload);
  }, []);

  const notifyFailure = useCallback((title: string, err: unknown) => {
    const anyErr = err as
      | { response?: { data?: { detail?: string } }; message?: string }
      | undefined;
    const message =
      anyErr?.response?.data?.detail ||
      anyErr?.message ||
      'Unknown error occurred';
    setError({ title, message });
  }, []);

  const clear = useCallback(() => {
    setError(null);
    setSuccess(null);
  }, []);

  return { error, success, notifyError, notifySuccess, notifyFailure, clear };
}


interface Props {
  error: NotifyPayload | null;
  success: NotifyPayload | null;
  onClearError: () => void;
  onClearSuccess: () => void;
  /** Milliseconds before the error toast auto-dismisses. Default 8000. */
  errorAutoDismissMs?: number;
  /** Milliseconds before the success toast auto-dismisses. Default 4000. */
  successAutoDismissMs?: number;
}


/** Render both the error and success toasts (if present) inside a single
 *  positioned NotificationContainer. Cheap, top-right by default, dismissible
 *  via `onClearError` / `onClearSuccess`. */
export default function PanelNotifications({
  error,
  success,
  onClearError,
  onClearSuccess,
  errorAutoDismissMs = 8000,
  successAutoDismissMs = 4000,
}: Props) {
  if (!error && !success) return null;
  return (
    <NotificationContainer position="top-right">
      {error && (
        <Notification
          type="error"
          title={error.title}
          message={error.message}
          onClose={onClearError}
          autoDismiss={errorAutoDismissMs}
        />
      )}
      {success && (
        <Notification
          type="success"
          title={success.title}
          message={success.message}
          onClose={onClearSuccess}
          autoDismiss={successAutoDismissMs}
        />
      )}
    </NotificationContainer>
  );
}
